import React, { useState, useEffect } from 'react'
import { useTheme } from 'styled-components'
import { useSelector } from 'react-redux'
import {
    GuestUserModal,
    useToast,
    Text,
    RightSidePanel
} from 'shared-ui/components'
import {
    BookmarkIcon,
    reportIcon,
    accountIcon,
    SavedContent,
    DownloadIcon
} from 'shared-ui/assets'
import {
    useSaveContent,
    useDiscoveDeleteContent,
    useContentAnalytics
} from 'shared-ui/hooks'
import { getGuestUser } from 'shared-ui/utils/localStorage'
import {
    SUCCESS_MESSAGE,
    ERROR_MESSAGES,
    QUERY_KEY,
    PROFILE_CONSTANTS,
    SUPER_APP_CONFIG,
    DASHBOARD_MESSAGES
} from 'shared-ui/utils/constants'
import {
    DropdownMenu,
    DropdownContent,
    DropdownOption,
    AboutAccountDetails,
    AboutAccountRow
} from '../Profile/styles'
import { format } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'
import { GA_EVENTS } from 'shared-ui/utils/gaConstants'
import { useTrackEvent } from 'shared-ui/utils/analyticEvents'

const DesktopMoreOptions = ({
    data,
    user,
    onClose,
    onReport,
    onAbout,
    style = {},
    hideSave = false,
    fromDashboard = false,
    fromNotYetPublished = false,
    onDownload,
    onSaveToggle,
    setShowOtherOptions,
    desktopMenuOpen=true,
    isCurrentUser
}) => {
    const theme = useTheme()
    const { showError, showSuccess } = useToast()
    const [dataVal, setData] = useState(data)
    const [showGuestModal, setShowGuestModal] = useState(false)
    const [showAbout, setShowAbout] = useState(false)
    const { ai_download_enabled } = useSelector((state) => state.configuration);
    const queryClient = useQueryClient()
    const trackEvent = useTrackEvent()
    const { dispatchInteractionEvent } = useContentAnalytics()
    const toolName = data?.ai_toolname || data?.custom_content?.tool_name
    const showDownload =
      ai_download_enabled?.supported_tools?.includes(toolName)
        ? ai_download_enabled.enabled
        : true;

    useEffect(() => {
        const normalizeData = (d) => {
            if (!d) return d
            return {
                ...d,
                content_id: d?.content_id || d?.id || d?.contentId,
                user_id: d?.user_id || d?.userId || d?.creator_id
            }
        }
        setData(normalizeData(data))
    }, [data])

    const { mutateAsync: saveContentMutate, isLoading: saveLoading } =
        useSaveContent(user?.user_id, {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    'GET_USER_DASHBOARD',
                    user?.user_id,
                    'saved',
                    data?.content_type,
                    'saved'
                )
                showSuccess(SUCCESS_MESSAGE?.SAVED_TO_DASHBOARD || 'Saved to My Dashboard')
            },
            onError: (err) => {
                const parsed = JSON.parse(err?.message || '{}')
                showError(parsed || ERROR_MESSAGES.FAILED_TO_SAVE)
            }
        })

    const { mutate: deleteMutation } = useDiscoveDeleteContent({
        onSuccess: (resp) => {
            queryClient.invalidateQueries('GET_USER_DASHBOARD')
        },
        onError: (err) => {
            showError(err?.message || ERROR_MESSAGES.FAILED_TO_REMOVE_SAVES)
        }
    })

    const checkGuest = () => {
        if (getGuestUser() === 'true') {
            setShowGuestModal(true)
            return true
        } else {
            return false
        }
    }

    const handleSave = async (content_id, user_id) => {
        if (checkGuest()) return
        if (saveLoading) return
        if (!user?.user_id) {
            showError(ERROR_MESSAGES.LOGIN_TO_SAVE)
            return
        }
        const cid = content_id || dataVal?.content_id || dataVal?.id
        const uid = user_id || dataVal?.user_id || dataVal?.userId

        if (!cid) {
            showError(ERROR_MESSAGES.CONTENT_NOT_FOUND)
            return
        }
        trackEvent(GA_EVENTS.content_saved, {
            content_id: cid,
            creator_id: uid
        })

        try {
            const res = await saveContentMutate(cid)
            dispatchInteractionEvent('content_saved', { ...dataVal, content_id: cid })
            queryClient.invalidateQueries(QUERY_KEY.GET_DISCOVER_DATA)
            queryClient.invalidateQueries(['GET_CONTENT_DETAILS_BY_ID', cid])
            queryClient.invalidateQueries(['GET_SAVED_CONTENT'])
            onClose && onClose()
        } catch {
            console.error('Failed to save content')
        }
    }

    const handleUnSave = async (content_id) => {
        if (checkGuest()) return
        if (saveLoading) return
        if (!user?.user_id) {
            showError(ERROR_MESSAGES.LOGIN_TO_SAVE)
            return
        }
        const cid = content_id || dataVal?.content_id || dataVal?.id
        if (!cid) {
            showError(ERROR_MESSAGES.CONTENT_NOT_FOUND)
            return
        }

        try {
            const res = await deleteMutation(cid)
            queryClient.invalidateQueries(QUERY_KEY.GET_DISCOVER_DATA)
            queryClient.invalidateQueries(['GET_CONTENT_DETAILS_BY_ID', cid])
            queryClient.invalidateQueries(['GET_SAVED_CONTENT'])
            onClose && onClose()
        } catch {
            console.error('Failed to unsave content')
        }
    }

    const handleToggleSave = (is_saved, content_id, user_id) => {
        if (checkGuest()) return

        if (onSaveToggle) {
            onSaveToggle(is_saved, content_id, user_id)
            return
        }

        if (is_saved) handleUnSave(content_id)
        else handleSave(content_id, user_id)
    }

    const isOptionShow = !fromNotYetPublished && user?.user_id !== dataVal?.user_id;
    const reportKeyExit = dataVal?.hasOwnProperty('can_be_reported')?dataVal?.can_be_reported: true
    const canShowReport = reportKeyExit && isOptionShow
    const showDownloadButton = fromDashboard && onDownload && isCurrentUser && showDownload
    return (
        <>
            {desktopMenuOpen && !showAbout && (
                <DropdownMenu
                    style={{ marginTop: '13px', zIndex: 100, ...style }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <DropdownContent>
                        {!data?.is_ugc || !hideSave && !fromNotYetPublished && (
                            <DropdownOption
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    handleToggleSave(
                                        dataVal?.is_saved,
                                        dataVal?.content_id,
                                        dataVal?.user_id
                                    )
                                }}
                                style={{
                                    color: theme.app.iconlabel
                                }}
                            >
                                <img
                                    src={dataVal?.is_saved ? SavedContent : BookmarkIcon}
                                    style={{
                                        width: 18,
                                        height: 18,
                                        color: theme.app.iconlabel
                                    }}
                                    alt=""
                                />
                                {dataVal?.is_saved ? DASHBOARD_MESSAGES?.UNSAVE : SUPER_APP_CONFIG?.SAVE}
                            </DropdownOption>
                        )}

                        {showDownloadButton && (
                            <DropdownOption
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onDownload()
                                }}
                                style={{
                                    color: theme.app.iconlabel
                                }}
                            >
                                <img
                                    src={DownloadIcon} // Reuse bookmark icon if no download icon, but maybe I can use accountIcon or similar if better. Actually I'll just use a generic one if I can't find download.
                                    style={{
                                        width: 18,
                                        height: 18,
                                        color: theme.app.iconlabel,
                                    }}
                                    alt=""
                                />
                                {DASHBOARD_MESSAGES?.DOWNLOAD}
                            </DropdownOption>
                        )}

                        {isOptionShow && (
                            <DropdownOption
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (checkGuest()) return
                                    setShowAbout(true)
                                    setShowOtherOptions(true)
                                    // onClose && onClose()
                                }}
                                style={{
                                    color: theme.app.iconlabel
                                }}
                            >
                                <img
                                    src={accountIcon}
                                    style={{
                                        width: 18,
                                        height: 18,
                                        color: theme.app.iconlabel
                                    }}
                                    alt=""
                                />
                                {PROFILE_CONSTANTS?.TEXT?.ABOUT_ACCOUNT}
                            </DropdownOption>
                        )}

                        {canShowReport && (
                            <DropdownOption
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    if (checkGuest()) return
                                    onClose && onClose()
                                    onReport && onReport()
                                }}
                                style={{ color: '#FF4040' }}
                            >
                                <img
                                    src={reportIcon}
                                    style={{
                                        width: 18,
                                        height: 18
                                    }}
                                    alt=""
                                />
                                {PROFILE_CONSTANTS?.TEXT?.REPORT}
                            </DropdownOption>
                        )}
                    </DropdownContent>
                </DropdownMenu>
            )}

            <RightSidePanel
                open={showAbout}
                onClose={() => {
                    setShowAbout(false)
                    setShowOtherOptions(false)
                    // onClose && onClose()
                }}
                title={PROFILE_CONSTANTS?.TEXT?.ABOUT_ACCOUNT}
                width="400px"
            >
                <AboutAccountDetails>
                    <AboutAccountRow>
                        <Text color={theme?.app?.iconlabel} fontSize={'14px'}>
                            {PROFILE_CONSTANTS?.TEXT?.DATE_JOINED}
                        </Text>
                        <Text color={theme?.app?.text} fontSize={'14px'}>
                            {dataVal?.created_at
                                ? format(new Date(dataVal?.created_at), 'MMMM yyyy')
                                : 'N/A'}
                        </Text>
                    </AboutAccountRow>
                    <AboutAccountRow>
                        <Text color={theme?.app?.iconlabel} fontSize={'14px'}>
                            {PROFILE_CONSTANTS?.TEXT?.ACCOUNT_BASED_IN}
                        </Text>
                        <Text color={theme?.app?.text} fontSize={'14px'}>
                            IN
                        </Text>
                    </AboutAccountRow>
                </AboutAccountDetails>
            </RightSidePanel>

            {showGuestModal && (
                <GuestUserModal onClose={() => setShowGuestModal(false)} />
            )}
        </>
    )
}

export default DesktopMoreOptions
